import { useEffect, useState } from "react";
import { Plus, X } from "lucide-react";
import { toast } from "sonner";
import { http, errMsg, AVAIL_LABEL } from "@/lib/api";
import { F, inputCls, Card, SaveBar } from "@/components/admin/shared";
import { DAY_SHORT } from "@/lib/schedule";

const DAY_ORDER = [1, 2, 3, 4, 5, 6, 0];

const DayChips = ({ value = [], onChange, testId }) => (
  <div className="col-span-2 sm:col-span-5 flex flex-wrap gap-1" data-testid={testId}>
    {DAY_ORDER.map((d) => {
      const on = value.includes(d);
      return <button type="button" key={d} onClick={() => onChange(on ? value.filter((x) => x !== d) : [...value, d])} className={`rounded-lg px-2.5 py-1 text-[11px] font-semibold border ${on ? "bg-nanina-green text-white border-nanina-green" : "bg-white text-nanina-green border-nanina-line"}`} data-testid={`${testId}-${d}`}>{DAY_SHORT[d]}</button>;
    })}
  </div>
);

const IntervalSelect = ({ value, onChange, testId, inherit }) => (
  <select className={inputCls} value={value || 0} onChange={(e) => onChange(Number(e.target.value))} data-testid={testId}>
    {inherit && <option value={0}>Ikuti pengaturan umum</option>}
    <option value={30}>Setiap 30 menit</option>
    <option value={60}>Setiap 60 menit</option>
  </select>
);

const HoursEditor = ({ hours, onChange, testId }) => {
  const upd = (i, k, v) => onChange(hours.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  return (
    <div className="sm:col-span-2 space-y-2" data-testid={testId}>
      {hours.map((h, i) => (
        <div key={i} className="rounded-xl border border-nanina-line p-2 grid grid-cols-2 sm:grid-cols-[1.4fr_.8fr_.8fr_1fr_auto] gap-2 items-center">
          <input className={`${inputCls} !mt-0 col-span-2 sm:col-span-1`} value={h.days} placeholder="Hari, contoh: Senin – Jumat" onChange={(e) => upd(i, "days", e.target.value)} data-testid={`${testId}-days-${i}`} />
          <input type="time" className={`${inputCls} !mt-0`} value={h.open} onChange={(e) => upd(i, "open", e.target.value)} aria-label="Jam buka" data-testid={`${testId}-open-${i}`} />
          <input type="time" className={`${inputCls} !mt-0`} value={h.close} onChange={(e) => upd(i, "close", e.target.value)} aria-label="Jam tutup" data-testid={`${testId}-close-${i}`} />
          <input className={`${inputCls} !mt-0`} value={h.note} placeholder="Catatan, contoh: Tutup" onChange={(e) => upd(i, "note", e.target.value)} data-testid={`${testId}-note-${i}`} />
          <button type="button" onClick={() => onChange(hours.filter((_, j) => j !== i))} className="p-2 text-red-600" aria-label="Hapus baris" data-testid={`${testId}-remove-${i}`}><X className="h-4 w-4" /></button>
          <DayChips value={h.weekdays || []} onChange={(v) => upd(i, "weekdays", v)} testId={`${testId}-weekdays-${i}`} />
        </div>
      ))}
      <button type="button" onClick={() => onChange([...hours, { days: "", weekdays: [], open: "", close: "", note: "" }])} className="inline-flex items-center gap-1 text-xs font-semibold text-nanina-green" data-testid={`${testId}-add`}><Plus className="h-3.5 w-3.5" /> Tambah baris jadwal</button>
    </div>
  );
};

const StatusSelect = ({ value, onChange, testId }) => (
  <select className={inputCls} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testId}>
    <option value="">Tidak ditampilkan</option>
    {Object.entries(AVAIL_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
  </select>
);

export const HoursPanel = ({ categories, mode = "jadwal" }) => {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { http.get("/admin/settings").then((r) => setS(r.data)); }, []);
  if (!s) return <p className="text-sm text-nanina-muted">Memuat...</p>;
  const sched = s.service_schedules || {};
  const setSched = (cid, patch) => setS({ ...s, service_schedules: { ...sched, [cid]: { status: "", hours: [], notice: "", interval: 0, ...sched[cid], ...patch } } });
  const isJadwal = mode === "jadwal";

  const save = async (publish) => {
    setBusy(true);
    try {
      await http.put("/admin/settings", { operating_hours: s.operating_hours || [], service_status: s.service_status || "", service_status_note: s.service_status_note || "", service_schedules: sched, slot_interval: s.slot_interval || 60 });
      if (publish) await http.post("/admin/settings/publish");
      const r = await http.get("/admin/settings");
      setS(r.data);
      toast.success(publish ? "Jam operasional diterbitkan" : "Draft jam operasional disimpan");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4 pb-24" data-testid={`hours-panel-${mode}`}>
      <p className="text-sm text-nanina-muted">{isJadwal
        ? "Pilih hari (Sen–Min) dan jam buka–tutup agar formulir WhatsApp otomatis menampilkan pilihan jam. Pilihan jam hanya permintaan awal, bukan konfirmasi slot. Bila jadwal kosong, pengunjung melihat \"Hubungi admin untuk informasi jadwal\"."
        : "Atur status yang tampil di website: Tersedia, Jadwal Penuh, Tutup Sementara, atau Tutup."}</p>
      <Card title={isJadwal ? "Jadwal umum Nanina Care" : "Status umum Nanina Care"}>
        {isJadwal ? (
          <>
            <F label="Interval pilihan jam di formulir"><IntervalSelect value={s.slot_interval || 60} onChange={(v) => setS({ ...s, slot_interval: v })} testId="hours-global-interval" /></F>
            <HoursEditor hours={s.operating_hours || []} onChange={(h) => setS({ ...s, operating_hours: h })} testId="hours-global" />
          </>
        ) : (
          <>
            <F label="Status umum"><StatusSelect value={s.service_status} onChange={(v) => setS({ ...s, service_status: v })} testId="hours-global-status" /></F>
            <F label="Pengumuman sementara (opsional)" hint="Contoh: Libur Idulfitri 1–3 April"><input className={inputCls} value={s.service_status_note || ""} onChange={(e) => setS({ ...s, service_status_note: e.target.value })} data-testid="hours-global-note" /></F>
          </>
        )}
      </Card>
      {categories.map((c) => {
        const v = sched[c.id] || {};
        return (
          <Card key={c.id} title={`${isJadwal ? "Jadwal" : "Status"} ${c.name}`}>
            {isJadwal ? (
              <>
                <F label="Interval pilihan jam"><IntervalSelect inherit value={v.interval} onChange={(x) => setSched(c.id, { interval: x })} testId={`hours-interval-${c.slug}`} /></F>
                <p className="sm:col-span-2 text-[11px] text-nanina-muted">Kosongkan jadwal untuk mengikuti jadwal umum.</p>
                <HoursEditor hours={v.hours || []} onChange={(h) => setSched(c.id, { hours: h })} testId={`hours-${c.slug}`} />
              </>
            ) : (
              <>
                <F label="Status layanan"><StatusSelect value={v.status} onChange={(x) => setSched(c.id, { status: x })} testId={`hours-status-${c.slug}`} /></F>
                <F label="Info sementara (opsional)"><input className={inputCls} value={v.notice || ""} onChange={(e) => setSched(c.id, { notice: e.target.value })} data-testid={`hours-notice-${c.slug}`} /></F>
              </>
            )}
          </Card>
        );
      })}
      <SaveBar busy={busy} s={s} onDraft={() => save(false)} onPublish={() => save(true)} testId="hours" />
    </div>
  );
};
